import {
    ApiResponse,
    /** Auth */
    LoginProps,
    LoginResponse,
    VerifyEmailResponse,
    ExchangeGoogleTokenResponse,
    /** User */
    CreateUserProps,
    CreateUserResponse,
    EditUserDataProps,
    EditUserResponse,
    GetUserResponse,
    /** Billing */
    GetStripePricesResponse,
    CreateStripeSubscriptionProps,
    CreateStripeSubscriptionResponse,
    CreateStripeCheckoutSessionProps,
    CreateStripeCheckoutSessionResponse,
    VerifyCouponResponse,
    /** Path */
    GetPathResponse,
    GetPathsProgressResponse,
    GetPathsOverviewResponse,
    GetArticleResourceResponse,
    GetExternalResourceResponse,
    GetInformationalResourceResponse,
    GetCategoryResponse,
    GetCategoriesResponse,
    GetPathProgressResponse,
    GetQuizResourceResponse,
    GetQuizResultsResponse,
    GetResourceProps,
    GetResourcesOverviewResponse,
    GetLessonResourceResponse,
    GetProjectResourceResponse,
    GetVideoResourceResponse,
    MarkResourseAsDoneProps,
    GetPathNextResourceResponse,
    /** Tracks and Courses */
    GetCourseResponse,
    GetCourseProgressResponse,
    GetCoursesOverviewResponse,
    GetCoursesProgressResponse,
    SetResourceCompletionStatusResponse,
} from 'utils/devv-sdk/types'

import {
    DomainOverview,
    Language,
    LanguageOverview,
    Role,
    RoleOverview,
    Subject,
    SubjectOverview,
    Tool,
    ToolOverview,
} from 'types/wiki'

import axios from 'axios'

const DevvAPI = axios.create({
    baseURL: process.env.NEXT_PUBLIC_DEVV_API_URL,
})

export default DevvAPI

export const setAuthToken = (token: string) => {
    DevvAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

/** Auth */

export async function loginEmailPassword({ email, password }: LoginProps): Promise<LoginResponse> {
    try {
        const resp = await DevvAPI.post<LoginResponse>(`auth/login`, {
            email,
            password,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function exchangeGoogleToken(value: string): Promise<ExchangeGoogleTokenResponse> {
    try {
        const resp = await DevvAPI.post<ExchangeGoogleTokenResponse>(`auth/google/token`, {
            id_token: value,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

/** User */

export async function createUser({
    email,
    password,
    first_name,
    last_name,
}: CreateUserProps): Promise<CreateUserResponse> {
    try {
        const resp = await DevvAPI.post<CreateUserResponse>('users', {
            email,
            password,
            first_name,
            last_name,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getUser(userId: string): Promise<GetUserResponse> {
    try {
        if (!userId) throw new Error('No user id provided')

        const resp = await DevvAPI.get<GetUserResponse>(`users/${userId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getUserById(userId: string, token: string): Promise<GetUserResponse> {
    try {
        if (!userId) throw new Error('No user id provided')
        if (!token) throw new Error('No token provided')

        const resp = await DevvAPI.get<GetUserResponse>(`users/${userId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function editUserData(userId: string, props: EditUserDataProps): Promise<EditUserResponse> {
    try {
        if (!userId) throw new Error('User ID is required')

        const resp = await DevvAPI.patch<EditUserResponse>(`users/${userId}`, props)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function startPasswordReset(value: string): Promise<void> {
    try {
        await DevvAPI.put('users/password/reset', {
            email: value,
        })
    } catch (error) {
        throw error
    }
}

export async function passwordReset(value: { token: string; password: string }): Promise<void> {
    try {
        await DevvAPI.patch('users/password', value)
    } catch (error) {
        throw error
    }
}

export async function sendVerificationEmail(email: string): Promise<void> {
    try {
        await DevvAPI.put('users/email/verify', { email })
    } catch (error) {
        throw error
    }
}

export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
    try {
        const resp = await DevvAPI.put<VerifyEmailResponse>(`users/email/verify/${token}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function emailReset(userId: string, value: string): Promise<void> {
    try {
        if (!userId) throw new Error('User ID is required')
        await DevvAPI.put(`users/${userId}/email/reset`, { email: value })
    } catch (error) {
        throw error
    }
}

/** Sorting Quiz */

export async function getQuizResults(typformResponseId: string): Promise<GetQuizResultsResponse> {
    try {
        const resp = await DevvAPI.get<GetQuizResultsResponse>(`sorting-quiz/results/${typformResponseId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

/** Categories */

export async function getCategories(): Promise<GetCategoriesResponse> {
    try {
        const resp = await DevvAPI.get<GetCategoriesResponse>('categories')

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getCategoryBySlug(value: string): Promise<GetCategoryResponse> {
    try {
        const resp = await DevvAPI.get<GetCategoryResponse>(`categories/by-slug/${value}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

/** Paths */

export async function getPathsOverview(query?: {
    live?: boolean
    categories?: string[]
}): Promise<GetPathsOverviewResponse> {
    try {
        const defaultParams = { published: true }
        const params = {
            ...defaultParams,
            categories: query?.categories ? JSON.stringify(query.categories) : undefined,
            live: query?.live,
        }

        const resp = await DevvAPI.get<GetPathsOverviewResponse>('paths/overview', { params })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getPathsProgress(): Promise<GetPathsProgressResponse> {
    try {
        const resp = await DevvAPI.get<GetPathsProgressResponse>('paths/progress')

        return resp?.data
    } catch (error) {
        throw error
    }
}

/** Path */

export async function getPathById(pathId: string): Promise<GetPathResponse> {
    try {
        const resp = await DevvAPI.get<GetPathResponse>(`paths/${pathId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getPathBySlug(slug: string): Promise<GetPathResponse> {
    try {
        const resp = await DevvAPI.get<GetPathResponse>(`paths/by-slug/${slug}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getPathProgress(pathId: string): Promise<GetPathProgressResponse> {
    try {
        const resp = await DevvAPI.get<GetPathProgressResponse>(`paths/${pathId}/progress`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function enrollUserInPath(pathId: string, email?: string): Promise<void> {
    try {
        await DevvAPI.put(`paths/${pathId}/enroll`, { email })
    } catch (error) {
        throw error
    }
}

export async function preEnrollUserInPath(pathId: string, email?: string): Promise<void> {
    try {
        await DevvAPI.put(`paths/${pathId}/pre-enroll`, { email })
    } catch (error) {
        throw error
    }
}

export async function getPathNextResource(props: {
    pathId: string
    currentResourceId: string
}): Promise<GetPathNextResourceResponse> {
    try {
        const { pathId, currentResourceId } = props
        const resp = await DevvAPI.get<GetPathNextResourceResponse>(
            `paths/${pathId}/resource-after/${currentResourceId}`,
        )

        return resp?.data
    } catch (error) {
        throw error
    }
}

/** Tracks And Courses */

export async function getCoursesOverview(query?: {
    live?: boolean
    categories?: string[]
}): Promise<GetCoursesOverviewResponse> {
    try {
        const defaultParams = { course: true, published: true }

        const params = JSON.parse(
            JSON.stringify({
                ...defaultParams,
                categories: query?.categories ? JSON.stringify(query.categories) : undefined,
                live: query?.live,
            }),
        )

        const resp = await DevvAPI.get<GetCoursesOverviewResponse>(`tracks/overview`, {
            params,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getCoursesProgress(): Promise<GetCoursesProgressResponse> {
    try {
        const resp = await DevvAPI.get<GetCoursesProgressResponse>(`tracks/progress`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getCourseById(courseId: string): Promise<GetCourseResponse> {
    try {
        const resp = await DevvAPI.get<GetCourseResponse>(`tracks/${courseId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getCourseBySlug(slug: string): Promise<GetCourseResponse> {
    try {
        const resp = await DevvAPI.get<GetCourseResponse>(`tracks/by-slug/${slug}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getCourseProgress(courseId: string): Promise<GetCourseProgressResponse> {
    try {
        const resp = await DevvAPI.get<GetCourseProgressResponse>(`tracks/${courseId}/progress`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function enrollUserInCourse(courseId: string, email?: string): Promise<void> {
    try {
        const resp = await DevvAPI.put<void>(`tracks/${courseId}/enroll`, { email })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function preEnrollUserInCourse(courseId: string, email?: string): Promise<void> {
    try {
        const resp = await DevvAPI.put<void>(`tracks/${courseId}/pre-enroll`, { email })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getCourseNextResource(props: {
    courseId: string
    currentResourceId: string
}): Promise<GetPathNextResourceResponse> {
    try {
        const { courseId, currentResourceId } = props

        const resp = await DevvAPI.get<GetPathNextResourceResponse>(
            `tracks/${courseId}/resource-after/${currentResourceId}`,
        )

        return resp?.data
    } catch (error) {
        throw error
    }
}

/** Resources */

export async function getResourcesOverview(resources: string[]): Promise<GetResourcesOverviewResponse> {
    try {
        const resp = await DevvAPI.get<GetResourcesOverviewResponse>('resources/overview', {
            params: {
                ids: JSON.stringify(resources),
            },
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function setResourceCompletionStatus(
    props: MarkResourseAsDoneProps,
): Promise<SetResourceCompletionStatusResponse> {
    try {
        const { resourceId, done } = props

        const resp = await DevvAPI.put<SetResourceCompletionStatusResponse>(
            `resources/${resourceId}/${done ? 'done' : 'todo'}`,
        )

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getInformationalResourceDetails(
    props: GetResourceProps,
): Promise<GetInformationalResourceResponse> {
    const { resourceId } = props
    try {
        const resp = await DevvAPI.get<GetInformationalResourceResponse>(`resources/informationals/${resourceId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getArticleResourceDetails(props: GetResourceProps): Promise<GetArticleResourceResponse> {
    const { resourceId } = props
    try {
        const resp = await DevvAPI.get<GetArticleResourceResponse>(`resources/articles/${resourceId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getLessonResourceDetails(lessonId: string): Promise<GetLessonResourceResponse> {
    try {
        const resp = await DevvAPI.get<GetLessonResourceResponse>(`resources/lessons/${lessonId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getVideoResourceDetails(props: GetResourceProps): Promise<GetVideoResourceResponse> {
    const { resourceId } = props
    try {
        const resp = await DevvAPI.get<GetVideoResourceResponse>(`resources/videos/${resourceId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getExternalResourceDetails(props: GetResourceProps): Promise<GetExternalResourceResponse> {
    const { resourceId } = props
    try {
        const resp = await DevvAPI.get<GetExternalResourceResponse>(`resources/external-resources/${resourceId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getQuizResourceDetails(props: GetResourceProps): Promise<GetQuizResourceResponse> {
    const { resourceId } = props
    try {
        const resp = await DevvAPI.get<GetQuizResourceResponse>(`resources/quizzes/${resourceId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getProjectResourceDetails(props: GetResourceProps): Promise<GetProjectResourceResponse> {
    const { resourceId } = props
    try {
        const resp = await DevvAPI.get<GetProjectResourceResponse>(`resources/projects/${resourceId}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function setExerciseCheckpointCompletionStatus(
    exerciseId: string,
    done: boolean,
    checkpointId?: string,
): Promise<SetResourceCompletionStatusResponse> {
    try {
        const resp = await DevvAPI.put<SetResourceCompletionStatusResponse>(
            `exercises/${exerciseId}/checkpoints/${done ? 'done' : 'todo'}`,
            {
                data: {
                    checkpoint: checkpointId,
                },
            },
        )

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getExerciseProgress(
    exerciseId: string,
): Promise<ApiResponse<{ exercise: string; completed: boolean; checkpoints_completed: string[] }>> {
    try {
        const resp = await DevvAPI.get<
            ApiResponse<{ exercise: string; completed: boolean; checkpoints_completed: string[] }>
        >(`exercises/${exerciseId}/progress`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getLessonProgress(lessonId: string): Promise<
    ApiResponse<{
        exercises: {
            _id: string
            completed: boolean
            checkpoints_completed: string[]
        }[]
        completed: boolean
        current_exercise: string | null
        current_exercise_number: number | null
    }>
> {
    try {
        const resp = await DevvAPI.get<
            ApiResponse<{
                exercises: {
                    _id: string
                    completed: boolean
                    checkpoints_completed: string[]
                }[]
                completed: boolean
                current_exercise: string | null
                current_exercise_number: number | null
            }>
        >(`resources/lessons/${lessonId}/progress`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getProjectProgress(
    projectId: string,
): Promise<ApiResponse<{ completed: boolean; tasks_completed: string[] }>> {
    try {
        const resp = await DevvAPI.get<ApiResponse<{ completed: boolean; tasks_completed: string[] }>>(
            `resources/${projectId}/progress`,
        )

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function setProjectTaskCompletionStatus(
    projectId: string,
    done: boolean,
    taskId: string,
): Promise<SetResourceCompletionStatusResponse> {
    try {
        const resp = await DevvAPI.put<SetResourceCompletionStatusResponse>(
            `resources/projects/${projectId}/tasks/${taskId}/${done ? 'done' : 'todo'}`,
        )

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function setResourceStarted(resourceId: string): Promise<void> {
    try {
        const resp = await DevvAPI.put<void>(`resources/${resourceId}/start`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function canUserAccessResource(props: {
    resourceId: string
    userId: string
}): Promise<ApiResponse<{ can_access: boolean }>> {
    try {
        const resp = await DevvAPI.get<ApiResponse<{ can_access: boolean }>>(
            `users/${props.userId}/can-access/${props.resourceId}`,
        )

        return resp?.data
    } catch (error) {
        throw error
    }
}

/** Billing */

export async function getStripePrices(token: string): Promise<GetStripePricesResponse> {
    try {
        const resp = await DevvAPI.get<GetStripePricesResponse>('billing/prices', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function verifyCoupon(coupon: string, token: string): Promise<VerifyCouponResponse> {
    try {
        const resp = await DevvAPI.get<VerifyCouponResponse>(`billing/promo-codes/${coupon}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function createStripeSubscription(
    props: CreateStripeSubscriptionProps,
    token: string,
): Promise<CreateStripeSubscriptionResponse> {
    try {
        const resp = await DevvAPI.post<CreateStripeSubscriptionResponse>('billing/stripe/subscriptions', props, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function createStripeCheckoutSession(
    props: CreateStripeCheckoutSessionProps,
    token: string,
): Promise<CreateStripeCheckoutSessionResponse> {
    try {
        const resp = await DevvAPI.post<CreateStripeCheckoutSessionResponse>(
            'billing/stripe/checkout-sessions',
            props,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        )

        return resp?.data
    } catch (error) {
        throw error
    }
}

/** Wiki */

export async function getDomainsOverview(query?: { limit?: number }): Promise<ApiResponse<DomainOverview[]>> {
    try {
        const defaultParams = {}

        const params = {
            ...defaultParams,
            ...query,
        }

        const resp = await DevvAPI.get<ApiResponse<DomainOverview[]>>('wiki/domains/overview', {
            params,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getRolesOverview(query?: { limit?: number }): Promise<ApiResponse<RoleOverview[]>> {
    try {
        const defaultParams = {}

        const params = {
            ...defaultParams,
            ...query,
        }

        const resp = await DevvAPI.get<ApiResponse<RoleOverview[]>>('wiki/roles/overview', {
            params,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getRoles(query?: { limit?: number }): Promise<ApiResponse<Role[]>> {
    try {
        const defaultParams = {}

        const params = {
            ...defaultParams,
            ...query,
        }

        const resp = await DevvAPI.get<ApiResponse<Role[]>>('wiki/roles', {
            params,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getRoleBySlug(slug: string): Promise<ApiResponse<Role>> {
    try {
        const resp = await DevvAPI.get<ApiResponse<Role>>(`wiki/roles/by-slug/${slug}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getLanguagesOverview(query?: { limit?: number }): Promise<ApiResponse<LanguageOverview[]>> {
    try {
        const defaultParams = {}

        const params = {
            ...defaultParams,
            ...query,
        }

        const resp = await DevvAPI.get<ApiResponse<LanguageOverview[]>>('wiki/languages/overview', {
            params,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getLanguageBySlug(slug: string): Promise<ApiResponse<Language>> {
    try {
        const resp = await DevvAPI.get<ApiResponse<Language>>(`wiki/languages/by-slug/${slug}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getToolsOverview(query?: { limit?: number }): Promise<ApiResponse<ToolOverview[]>> {
    try {
        const defaultParams = {}

        const params = {
            ...defaultParams,
            ...query,
        }

        const resp = await DevvAPI.get<ApiResponse<ToolOverview[]>>('wiki/tools/overview', {
            params,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getToolBySlug(slug: string): Promise<ApiResponse<Tool>> {
    try {
        const resp = await DevvAPI.get<ApiResponse<Tool>>(`wiki/tools/by-slug/${slug}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getSubjectsOverview(query?: { limit?: number }): Promise<ApiResponse<SubjectOverview[]>> {
    try {
        const defaultParams = {}

        const params = {
            ...defaultParams,
            ...query,
        }

        const resp = await DevvAPI.get<ApiResponse<SubjectOverview[]>>('wiki/subjects/overview', {
            params,
        })

        return resp?.data
    } catch (error) {
        throw error
    }
}

export async function getSubjectBySlug(slug: string): Promise<ApiResponse<Subject>> {
    try {
        const resp = await DevvAPI.get<ApiResponse<Subject>>(`wiki/subjects/by-slug/${slug}`)

        return resp?.data
    } catch (error) {
        throw error
    }
}
